import React, { FC, useState } from 'react';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Cart, LineItem } from 'shared/types/cart';
import { CloudinaryProductAsset, ContentfulProduct } from 'shared/types/content';
import { Variant } from 'shared/types/product';
import { ExtendedProductDetailProduct } from 'shared/types/product/Product';
import { LineItem as WishListLineItem, Wishlist } from 'shared/types/wishlist';
import usePath from 'helpers/hooks/usePath';
import usePreloadImages from 'helpers/hooks/usePreloadImages';
import { toUIProduct } from 'helpers/mappers/toUIProduct';
import { toUIProductContent } from 'helpers/mappers/toUIProductContent';
import ProductDetails, { ProductDetailsProps } from '..';
import { getProductParentCategory } from './getProductParentCategory';
import RichText from "../../../../atoms/rich-text";

type ProductDetailsAdapterProps = {
  addToWishlist?: (lineItem: WishListLineItem, count: number) => Promise<void>;
  assets?: CloudinaryProductAsset[];
  cart?: Cart;
  content?: ContentfulProduct;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  onAddToCart?: (
    variant: Variant,
    quantity: number,
    finish?: string,
    sheen?: string,
    hasWirebrush?: boolean,
  ) => Promise<Cart>;
  origin?: string;
  product: ExtendedProductDetailProduct;
  removeLineItem?: (item: LineItem) => Promise<void>;
  reviewListProps?: ProductDetailsProps['reviewListProps'];
  setIsOpen?: (value: boolean) => void;
  wirebrushModal?: ProductDetailsProps['wirebrushModal'];
  wishlist?: Wishlist;
  woodFinishHelper?: ProductDetailsProps['woodFinishHelper'];
};

const ProductDetailsAdapter: FC<ProductDetailsAdapterProps> = ({
  product,
  content,
  assets,
  woodFinishHelper,
  wirebrushModal,
  inModalVersion,
  wishlist,
  cart,
  setIsOpen,
  onAddToCart,
  removeLineItem,
  reviewListProps,
  origin,
}) => {
  const { path } = usePath();
  const category = getProductParentCategory(product);

  const [variant, setVariant] = useState(() => {
    if (inModalVersion) {
      return product?.variants[0];
    } else {
      const currentVariantPath = path.split('/');

      // access position 3 since variantPath = ['', 'slug', 'p', 'sku', '']
      const currentVariantSKU = currentVariantPath[3]?.split('?')[0];

      const currentVariantIndex = product?.variants.findIndex(({ sku }) => sku == currentVariantSKU);

      return product.variants[currentVariantIndex] ?? product.variants?.[0];
    }
  });

  const inCartQuantity = cart?.lineItems?.find((lineItem) => lineItem.variant?.sku === variant.sku)?.count ?? 0;

  usePreloadImages(
    product.variants
      .map((variant) => variant.images ?? [])
      .flat() /* , 'large' TODO: Commenting this for now so images show up. We need to implement the right Cloudinary strat. eventually */,
  );

  const mappedProduct = toUIProduct(product, variant);
  const mappedContent = toUIProductContent(content, assets);

  const handleChangeVariant = (sku: string) => {
    const variantsToUse = product.variants.find((variant) => variant.sku === sku);

    if (variantsToUse) setVariant(variantsToUse);
  };

  const description = mappedContent?.description ? (
    <RichText content={mappedContent.description} variant="small" className="text-text-tertiary" />
  ) : (
    <p className="text-14 font-regular leading-loose tracking-normal text-text-tertiary [&_ul]:p-0 [&_ul]:[list-style:inside_circle]">
      {product.description}
    </p>
  );

  const plainTextDescription = mappedContent?.description
    ? documentToPlainTextString(mappedContent.description)
    : product.description;

  return (
    <ProductDetails
      product={{
        ...mappedProduct,
        images: mappedContent.images?.length ? mappedContent.images : [mappedProduct.images[0]],
      }}
      woodFinishHelper={woodFinishHelper}
      wirebrushModal={wirebrushModal}
      productContent={mappedContent}
      description={description}
      plainTextDescription={plainTextDescription}
      variant={variant}
      category={category}
      url={product._url}
      inModalVersion={inModalVersion}
      wishlist={wishlist}
      onChangeVariant={handleChangeVariant}
      setIsOpen={setIsOpen}
      onAddToCart={onAddToCart}
      removeLineItem={removeLineItem}
      inCartQuantity={inCartQuantity}
      reviewListProps={reviewListProps}
      origin={origin}
    />
  );
};

export default ProductDetailsAdapter;
